import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';

type VDRPermission = {
  room_id: string;
  access_level: 'viewer' | 'editor' | 'admin';
};

type DomainGroupPermission = {
  group_id: string;
  access_level: 'viewer' | 'editor' | 'admin';
};

type HoldingsCompanyPermission = {
  company_id: string;
  access_level: 'viewer' | 'editor' | 'admin';
};

type PermissionContextType = {
  isAdmin: boolean;
  role: string;
  userId: string;

  // Hierarchical Permissions
  pages: string[]; // ['admin.home', 'fundraising.vdr', etc.]
  categories: string[]; // ['admin', 'fundraising']
  vdrPermissions: VDRPermission[];
  domainGroupPermissions: DomainGroupPermission[];
  holdingsCompanyPermissions: HoldingsCompanyPermission[];

  // Helpers
  hasCategory: (category: string) => boolean;
  hasPage: (fullKey: string) => boolean;
  hasVDRAccess: (roomId: string, minLevel?: 'viewer' | 'editor' | 'admin') => boolean;
  hasDomainGroupAccess: (groupId: string, minLevel?: 'viewer' | 'editor' | 'admin') => boolean;
  hasHoldingsCompanyAccess: (companyId: string, minLevel?: 'viewer' | 'editor' | 'admin') => boolean;
};

const PermissionContext = createContext<PermissionContextType>({
  isAdmin: false,
  role: 'user',
  userId: '',
  pages: [],
  categories: [],
  vdrPermissions: [],
  domainGroupPermissions: [],
  holdingsCompanyPermissions: [],
  hasCategory: () => false,
  hasPage: () => false,
  hasVDRAccess: () => false,
  hasDomainGroupAccess: () => false,
  hasHoldingsCompanyAccess: () => false,
});

export const usePermissions = () => useContext(PermissionContext);

export const PermissionProvider = ({ children }: { children: ReactNode }) => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [pages, setPages] = useState<string[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [vdrPermissions, setVdrPermissions] = useState<VDRPermission[]>([]);
  const [domainGroupPermissions, setDomainGroupPermissions] = useState<DomainGroupPermission[]>([]);
  const [holdingsCompanyPermissions, setHoldingsCompanyPermissions] = useState<HoldingsCompanyPermission[]>([]);
  const [userId, setUserId] = useState('');
  const [role, setRole] = useState('user');

  useEffect(() => {
    // Load from local storage
    const storedRole = localStorage.getItem("app_role") || 'user';
    const storedPermissions = localStorage.getItem("app_permissions");
    const storedId = localStorage.getItem("app_user_id") || '';
    
    setIsAdmin(storedRole === 'super_admin');
    setRole(storedRole);
    setUserId(storedId);

    if (storedRole === 'super_admin' || storedRole === 'admin') {
      // Super admin and admin have everything implicitly
      setPages([
        'admin.home', 'admin.users', 'admin.logs',
        'fundraising.vdr', 'fundraising.domain_groups',
        'fundraising.command_center', 'fundraising.analytics', 'fundraising.settings',
        'holdings.portfolio', 'holdings.costs', 'holdings.bank_imports',
        'holdings.planning', 'holdings.revenue'
      ]);
      setCategories(['admin', 'fundraising', 'holdings']);
      setVdrPermissions([]);
      setDomainGroupPermissions([]);
      setHoldingsCompanyPermissions([]); // Empty means full access for admins
    } else if (storedPermissions) {
      try {
        const parsed = JSON.parse(storedPermissions);
        const pPages = parsed.pages || [];
        setPages(pPages);
        setVdrPermissions(parsed.vdr || []);
        setDomainGroupPermissions(parsed.domainGroups || []);
        setHoldingsCompanyPermissions(parsed.holdingsCompanies || []);

        // Derive categories from pages + explicit category keys if any
        const cats = new Set<string>();
        pPages.forEach((p: string) => {
            if (!p.includes('.')) cats.add(p); // 'admin' is a category key itself
            else cats.add(p.split('.')[0]);
        });
        setCategories(Array.from(cats));
      } catch (e) {
        setPages([]);
        setCategories([]);
        setVdrPermissions([]);
        setDomainGroupPermissions([]);
        setHoldingsCompanyPermissions([]);
      }
    }
  }, []);

  const hasCategory = (category: string) => {
    if (isAdmin) return true;
    return categories.includes(category);
  };

  const hasPage = (fullKey: string) => {
    if (isAdmin) return true;
    return pages.includes(fullKey);
  };

  const hasVDRAccess = (roomId: string, minLevel: 'viewer' | 'editor' | 'admin' = 'viewer') => {
    if (isAdmin) return true;
    const perm = vdrPermissions.find(p => p.room_id === roomId);
    if (!perm) return false;
    
    const levels = { viewer: 1, editor: 2, admin: 3 };
    return levels[perm.access_level] >= levels[minLevel];
  };

  const hasDomainGroupAccess = (groupId: string, minLevel: 'viewer' | 'editor' | 'admin' = 'viewer') => {
    if (isAdmin) return true;
    const perm = domainGroupPermissions.find(p => p.group_id === groupId);
    if (!perm) return false;

    const levels = { viewer: 1, editor: 2, admin: 3 };
    return levels[perm.access_level] >= levels[minLevel];
  };

  const hasHoldingsCompanyAccess = (companyId: string, minLevel: 'viewer' | 'editor' | 'admin' = 'viewer') => {
    // Admins have full access to all companies
    if (isAdmin || role === 'admin') return true;

    // Check if user has global holdings category access (legacy/global permission)
    if (hasCategory('holdings')) return true;

    // Check company-specific permission
    const perm = holdingsCompanyPermissions.find(p => p.company_id === companyId);
    if (!perm) return false;

    const levels = { viewer: 1, editor: 2, admin: 3 };
    return levels[perm.access_level] >= levels[minLevel];
  };

  return (
    <PermissionContext.Provider value={{
        isAdmin,
        role,
        userId,
        pages,
        categories,
        vdrPermissions,
        domainGroupPermissions,
        holdingsCompanyPermissions,
        hasCategory,
        hasPage,
        hasVDRAccess,
        hasDomainGroupAccess,
        hasHoldingsCompanyAccess
    }}>
      {children}
    </PermissionContext.Provider>
  );
};
